import { Alert, Badge, Group, Loader, Paper, Stack, Table, Text } from "@mantine/core";
import React from "react";
import { IProduct, ManagementConfig } from "types/productManagementTypes";
import {
  ProductFetchStatus,
  TIMEOUT_ENUM_SECONDS,
  onOff,
  plain,
  timeoutSeconds,
} from "./DeviceStatesPanel";
import { diffAgainstDefault } from "./defaultConfigMatch";

export type ComparisonStatus = "match" | "differ" | "unknown";

export interface ComparisonRow {
  label: string;
  applied: string;
  device: string;
  status: ComparisonStatus;
  source: "reported" | "stored";
}

const boolish = (value: unknown): boolean | undefined => {
  if (value === true || value === 1 || value === "1" || value === "true") return true;
  if (value === false || value === 0 || value === "0" || value === "false") return false;
  return undefined;
};

const numberish = (value: unknown): number | undefined => {
  if (value === undefined || value === null || String(value).trim() === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const textish = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined;
  const s = String(value).trim();
  return s === "" ? undefined : s;
};

// Device enum names and the config's numeric seconds are the same scale;
// 0 is the legacy "leave unchanged" spelling of -1 (see mergeStoredConfig).
const timeoutValue = (value: unknown): number | undefined => {
  if (value === undefined || value === null) return undefined;
  const raw = String(value).trim();
  if (Object.prototype.hasOwnProperty.call(TIMEOUT_ENUM_SECONDS, raw)) {
    return TIMEOUT_ENUM_SECONDS[raw];
  }
  const seconds = numberish(raw);
  return seconds === 0 ? -1 : seconds;
};

// Display the same normalized value the comparison uses, so a legacy 0 shows
// as "-1 (Never)" instead of appearing to differ from (or oddly match) the
// device's NEVER; unknown enum strings fall back to the raw value.
const timeoutDisplay = (value: unknown): string => {
  const seconds = timeoutValue(value);
  return seconds === undefined ? plain(value) : timeoutSeconds(seconds);
};

const compared = (
  applied: number | boolean | string | undefined,
  device: number | boolean | string | undefined,
): ComparisonStatus => {
  if (applied === undefined || device === undefined) return "unknown";
  return applied === device ? "match" : "differ";
};

export const buildComparisonRows = (
  config: Partial<ManagementConfig>,
  product: IProduct | null,
): ComparisonRow[] => {
  const states = (product?.states ?? {}) as Record<string, unknown>;
  const stored = (states.managementConfig ?? {}) as Partial<ManagementConfig>;

  const reportedRows: ComparisonRow[] = [
    {
      label: "Brightness",
      applied: plain(config.brightness),
      device: plain(states.brightness),
      status: compared(numberish(config.brightness), numberish(states.brightness)),
      source: "reported",
    },
    {
      label: "Sleep delay",
      applied: timeoutDisplay(config.sleepDelayTime),
      device: timeoutDisplay(states.sleepDelay),
      status: compared(timeoutValue(config.sleepDelayTime), timeoutValue(states.sleepDelay)),
      source: "reported",
    },
    {
      label: "Screen-off delay",
      applied: timeoutDisplay(config.screenOffDelayTime),
      device: timeoutDisplay(states.screenOffDelay),
      status: compared(
        timeoutValue(config.screenOffDelayTime),
        timeoutValue(states.screenOffDelay),
      ),
      source: "reported",
    },
    {
      label: "USB debugging",
      applied: onOff(config.usbDebugging),
      device: onOff(states.usbDebugging),
      status: compared(boolish(config.usbDebugging), boolish(states.usbDebugging)),
      source: "reported",
    },
    {
      label: "System update",
      applied: onOff(config.systemUpdateOn),
      device: onOff(states.systemUpdate),
      status: compared(boolish(config.systemUpdateOn), boolish(states.systemUpdate)),
      source: "reported",
    },
    {
      label: "Wi-Fi always on",
      applied: onOff(config.wifiAlwaysOn),
      device: onOff(states.wifiAlwaysOn),
      status: compared(boolish(config.wifiAlwaysOn), boolish(states.wifiAlwaysOn)),
      source: "reported",
    },
    {
      label: "Play boundary",
      applied: onOff(config.playBoundary),
      device: onOff(states.playBoundary),
      status: compared(boolish(config.playBoundary), boolish(states.playBoundary)),
      source: "reported",
    },
    {
      // The config holds a flag; the device reports its current home
      // launcher package, so the two are shown but never diffed.
      label: "App as home",
      applied: onOff(config.setAppAsHome),
      device: plain(states.appAsHome),
      status: "unknown",
      source: "reported",
    },
  ];

  const storedBool = (label: string, key: keyof ManagementConfig): ComparisonRow => ({
    label,
    applied: onOff(config[key]),
    device: onOff(stored[key]),
    status: compared(boolish(config[key]), boolish(stored[key])),
    source: "stored",
  });
  const storedNumber = (label: string, key: keyof ManagementConfig): ComparisonRow => ({
    label,
    applied: plain(config[key]),
    device: plain(stored[key]),
    status: compared(numberish(config[key]), numberish(stored[key])),
    source: "stored",
  });
  const storedText = (label: string, key: keyof ManagementConfig): ComparisonRow => ({
    label,
    applied: plain(config[key]),
    device: plain(stored[key]),
    status: compared(textish(config[key]), textish(stored[key])),
    source: "stored",
  });

  const storedRows: ComparisonRow[] = [
    storedText("Tracking settings", "trackingSettings"),
    storedNumber("IPD", "ipd"),
    storedNumber("Check timeout", "checktimeout"),
    storedNumber("Idle delay", "idleDelayTime"),
    storedNumber("Idle shutdown", "idleShutdownTime"),
    storedBool("Update OS after boot", "doOsUpdateAfterBooted"),
    storedBool("Skip downloads", "dontDownload"),
    storedBool("Install Auto releases", "appAutoUpdate"),
    storedText("OS version", "osVersion"),
    storedText("OS download URL", "osDownloadUrl"),
    storedText("OS download path", "osDownloadPath"),
    storedText("App download URL", "appDownloadUrl"),
    storedText("App download path", "appDownloadPath"),
  ];

  return [...reportedRows, ...storedRows];
};

const STATUS_BADGE: Record<ComparisonStatus, { color: string; label: string } | null> = {
  match: { color: "green", label: "Matches" },
  differ: { color: "orange", label: "Differs" },
  unknown: null,
};

const SourceHeaderRow: React.FC<{ title: string; description: string }> = ({
  title,
  description,
}) => (
  <Table.Tr>
    <Table.Td colSpan={4} style={{ background: "var(--oculera-surface-alt)" }}>
      <Text size="xs" c="dimmed" fw={600}>
        {title}
      </Text>
      <Text size="xs" c="dimmed">
        {description}
      </Text>
    </Table.Td>
  </Table.Tr>
);

const ComparisonRows: React.FC<{ rows: ComparisonRow[] }> = ({ rows }) => (
  <>
    {rows.map((row) => {
      const badge = STATUS_BADGE[row.status];
      return (
        <Table.Tr key={row.label}>
          <Table.Td>
            <Text size="sm" c="dimmed">
              {row.label}
            </Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm" style={{ wordBreak: "break-word" }}>
              {row.applied}
            </Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm" style={{ wordBreak: "break-word" }}>
              {row.device}
            </Text>
          </Table.Td>
          <Table.Td>
            {badge ? (
              <Badge
                variant="light"
                color={badge.color}
                size="sm"
                styles={{ root: { maxWidth: "none" }, label: { overflow: "visible" } }}
              >
                {badge.label}
              </Badge>
            ) : (
              <Text size="sm" c="dimmed">
                –
              </Text>
            )}
          </Table.Td>
        </Table.Tr>
      );
    })}
  </>
);

interface ConfigStateComparisonProps {
  config: Partial<ManagementConfig>;
  product: IProduct | null;
  status: ProductFetchStatus;
  // The default config this device matches, when there is one, so the panel can
  // say whether the applied config is still that default or a custom one.
  defaultConfig?: Partial<ManagementConfig> | null;
  defaultConfigName?: string;
  applied?: boolean;
}

// The product is fetched once by the parent modal and shared with the other
// panels, so opening a config does a single getProduct request.
const ConfigStateComparison: React.FC<ConfigStateComparisonProps> = ({
  config,
  product,
  status,
  defaultConfig,
  defaultConfigName,
  applied,
}) => {
  const states = (product?.states ?? {}) as Record<string, unknown>;
  const hasStates = Object.keys(states).length > 0;
  const defaultDiff = diffAgainstDefault(config, defaultConfig);
  const defaultLabel = defaultConfigName ? `"${defaultConfigName}"` : "the matched default config";
  const rows = buildComparisonRows(config, product);
  const reportedRows = rows.filter((r) => r.source === "reported");
  const storedRows = rows.filter((r) => r.source === "stored");
  const differCount = rows.filter((r) => r.status === "differ").length;

  return (
    <Paper withBorder radius="sm" p="md">
      <Stack gap="xs">
        <Stack gap={2}>
          <Text fw={600} size="sm">
            Assigned config vs current device state
          </Text>
          <Text size="xs" c="dimmed">
            &quot;Assigned config&quot; is the management configuration stored here for this device;
            &quot;Device state&quot; is what the headset last reported
            {product?.lastAccess
              ? ` (last access ${new Date(product.lastAccess).toLocaleString()})`
              : ""}
            .
          </Text>
        </Stack>
        {applied === false && (
          <Alert color="yellow" variant="light">
            This config has not been applied yet — the device has not confirmed it, so it reaches
            the headset the next time it connects. Differences below are expected until then.
          </Alert>
        )}
        {defaultConfig &&
          (defaultDiff.length === 0 ? (
            <Group gap="xs">
              <Badge variant="light" color="blue">
                Same as default
              </Badge>
              <Text size="xs" c="dimmed">
                The assigned config matches {defaultLabel} on every setting an admin can edit.
              </Text>
            </Group>
          ) : (
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <Badge variant="light" color="grape">
                Custom
              </Badge>
              <Text size="xs" c="dimmed">
                The assigned config was customized for this device: it differs from {defaultLabel}{" "}
                in {defaultDiff.join(", ")}.
              </Text>
            </Group>
          ))}
        {status === "loading" && (
          <Group gap="xs">
            <Loader size="xs" />
            <Text size="sm" c="dimmed">
              Loading device state…
            </Text>
          </Group>
        )}
        {status === "error" && (
          <Text size="sm" c="red">
            Failed to load the device state
          </Text>
        )}
        {status === "loaded" && !hasStates && (
          <Text size="sm" c="dimmed">
            The device has not reported its state yet, so there is nothing to compare.
          </Text>
        )}
        {status === "loaded" && hasStates && (
          <Stack gap="xs">
            {differCount > 0 ? (
              <Alert color="orange" variant="light">
                {differCount === 1
                  ? "1 setting differs between the assigned config and the device state."
                  : `${differCount} settings differ between the assigned config and the device state.`}
              </Alert>
            ) : (
              <Alert color="green" variant="light">
                No differences detected between the assigned config and the device state.
              </Alert>
            )}
            <Table verticalSpacing="xs" horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w="22%">Setting</Table.Th>
                  <Table.Th w="20%">Assigned config</Table.Th>
                  <Table.Th>Device state</Table.Th>
                  <Table.Th w={110} style={{ whiteSpace: "nowrap" }}>
                    Status
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <SourceHeaderRow
                  title="Live values read from the device"
                  description="The headset reads these back from the system on connect, so the Device state column is what the hardware is actually doing."
                />
                <ComparisonRows rows={reportedRows} />
                <SourceHeaderRow
                  title="Values the device does not report"
                  description="The headset never reads these back, so the Device state column shows what its own managementConfig.json holds instead."
                />
                <ComparisonRows rows={storedRows} />
              </Table.Tbody>
            </Table>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default ConfigStateComparison;
